import { inject, Injectable, OnDestroy } from '@angular/core';
import { timer, Unsubscribable } from 'rxjs';
import { SessionTimeoutComponent } from '@layout/session-timeout/components/session-timeout/session-timeout.component';
import { Router } from '@angular/router';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { DialogService } from '@kaseya-design-components';

@Injectable({
  providedIn: 'root',
})
export class UserActivityService implements OnDestroy {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly userActivityUiTimeoutMs = 345 * 1000 * 60;
  private lastActivity: Date;
  private userActivityUiTimer: Unsubscribable | null;

  constructor() {
    this.authorizationService.onLogin.subscribe(() => this.beginTracking());
    this.authorizationService.onLogout.subscribe(() => this.endTracking());
  }

  trackActivity(): void {
    this.lastActivity = new Date();
  }

  ngOnDestroy(): void {
    this.endTracking();
  }

  private beginTracking(): void {
    this.trackActivity();
    this.scheduleUserActivityUiCheck(this.userActivityUiTimeoutMs);
  }

  private endTracking(): void {
    this.clearUserActivityUiTimer();
  }

  private clearUserActivityUiTimer(): void {
    if (this.userActivityUiTimer) {
      this.userActivityUiTimer.unsubscribe();
      this.userActivityUiTimer = null;
    }
  }

  private scheduleUserActivityUiCheck(time: number): void {
    this.clearUserActivityUiTimer();
    this.userActivityUiTimer = timer(time).subscribe(() => this.checkIfTimeout());
  }

  private checkIfTimeout(): void {
    const now = new Date();
    const timeLeft = this.lastActivity.getTime() + this.userActivityUiTimeoutMs - now.getTime();
    if (timeLeft <= 0) {
      this.onTimeout();
      return;
    }

    this.scheduleUserActivityUiCheck(timeLeft);
  }

  private onTimeout(): void {
    if (!this.authorizationService.isAuthorized()) {
      return;
    }

    if (this.authorizationService.isRefreshTokenExpired) {
      this.router.navigateByUrl(ApplicationPath.Logout);
      return;
    }

    this.dialogService.dismissAll();

    const modalRef = this.dialogService.openDialog(SessionTimeoutComponent);

    modalRef.onConfirm().subscribe(() => {
      this.trackActivity();
      this.scheduleUserActivityUiCheck(this.userActivityUiTimeoutMs);
    });

    modalRef.onDismiss().subscribe(() => this.router.navigateByUrl(ApplicationPath.Logout));
  }
}
